import React from 'react';
import { Plus, ClipboardList, AlertCircle } from 'lucide-react';
import {
  ACTION_STATUS_LABELS,
  type EnterpriseAction,
} from '../../services/actionService';
import { actionCompletionPct, isActionOverdue } from '../../utils/issueProgress';

interface ProblemActionsTableProps {
  actions: EnterpriseAction[];
  assigneeNames: Record<string, string>;
  compact?: boolean;
  onRowClick: (action: EnterpriseAction) => void;
  onCreateAction: () => void;
}

export const ProblemActionsTable: React.FC<ProblemActionsTableProps> = ({
  actions,
  assigneeNames,
  compact = false,
  onRowClick,
  onCreateAction,
}) => {
  if (actions.length === 0) {
    return (
      <div className="rounded border border-slate-200 bg-slate-50 px-3 py-4 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="flex items-start gap-3">
          <div className="rounded bg-slate-200 p-2 dark:bg-slate-700"><ClipboardList size={16} className="text-slate-500" /></div>
          <div className="flex-1">
            <p className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">Nenhuma ação vinculada</p>
            <p className="mt-0.5 text-[10px] text-slate-500">Registre ações corretivas para tratar a causa raiz e acompanhar execução.</p>
            <ul className="mt-2 space-y-0.5 text-[10px] text-slate-400">
              <li>• Defina responsável e prazo</li>
              <li>• Acompanhe status e atrasos</li>
              <li>• Integre ao módulo Ações</li>
            </ul>
            <button type="button" onClick={onCreateAction} className="mt-3 flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-blue-700">
              <Plus size={12} /> Criar Primeira Ação
            </button>
          </div>
        </div>
      </div>
    );
  }

  const rows = compact ? actions.slice(0, 5) : actions;

  return (
    <div>
      <div className="overflow-x-auto rounded border border-slate-200 dark:border-slate-700">
        <table className="w-full min-w-[720px] text-left text-[11px]">
          <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50">
            <tr>
              <th className="px-2 py-1 font-semibold text-slate-500">Ação</th>
              <th className="px-2 py-1 font-semibold text-slate-500">Responsável</th>
              <th className="px-2 py-1 font-semibold text-slate-500">Prazo</th>
              <th className="px-2 py-1 font-semibold text-slate-500">Status</th>
              <th className="px-2 py-1 font-semibold text-slate-500">Atraso</th>
              <th className="px-2 py-1 font-semibold text-slate-500">Progresso</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map(action => {
              const pct = actionCompletionPct(action);
              const overdue = isActionOverdue(action);
              return (
                <tr key={action.id} onClick={() => onRowClick(action)} className="cursor-pointer bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800/50">
                  <td className="max-w-[180px] truncate px-2 py-1 font-medium">{action.title}</td>
                  <td className="px-2 py-1 text-slate-600">{action.assigned_to ? (assigneeNames[action.assigned_to] ?? '—') : '—'}</td>
                  <td className="px-2 py-1">{action.due_date ? new Date(action.due_date).toLocaleDateString('pt-BR') : '—'}</td>
                  <td className="px-2 py-1">{ACTION_STATUS_LABELS[action.status]}</td>
                  <td className="px-2 py-1">
                    {overdue ? (
                      <span className="flex items-center gap-0.5 font-semibold text-red-600"><AlertCircle size={10} /> Sim</span>
                    ) : '—'}
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      <div className="h-1 w-10 rounded-full bg-slate-200 dark:bg-slate-700"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
                      <span className="text-[10px] text-slate-400">{pct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={onCreateAction} className="mt-1.5 flex items-center gap-1 rounded border border-slate-200 px-2 py-0.5 text-[10px] font-medium hover:bg-slate-50 dark:border-slate-600">
        <Plus size={11} /> Nova Ação
      </button>
    </div>
  );
};
